<script setup lang="ts">
import { computed } from "vue";

import Tooltip from "~/components/dumb/Tooltip.vue";
import { InfoIcon } from "~/icons";
import { Entitlement, Placement, Subscription, UserStatus } from "~/shared/enums";
import type { User } from "~/shared/types";
import { useTenantStore } from "~/stores";
import { useUpgrade } from "~/utils/billing";
import { getItemCountText } from "~/utils/common";

const STATUS_TO_PRETTY_NAME = new Map([
  [UserStatus.PENDING_SUBSCRIPTION_UPGRADE, "Upgrade required"],
  [UserStatus.INVITED, "Invited"],
  [UserStatus.PENDING_EMAIL_VERIFICATION, "Invited"],
  [UserStatus.DEACTIVATED, "Removed"],
  [UserStatus.AGENT_DISABLED, "Disabled"],
]);

const props = defineProps<{
  row: User;
}>();

const tenantStore = useTenantStore();
const { getUpgradeDescription } = useUpgrade(tenantStore);

const statusText = computed(() => STATUS_TO_PRETTY_NAME.get(props.row.status));

const upgradeTooltipText = computed(() => {
  const userCountText = getItemCountText(tenantStore.getEntitlementValue(Entitlement.MAX_USERS) ?? 0, "user");
  return getUpgradeDescription(`invite more than ${userCountText} to your workspace`, Subscription.APP_SUMO);
});
</script>

<template>
  <div class="flex items-center gap-1">
    <span
      v-if="statusText"
      class="text-sm"
      :class="row.status === UserStatus.PENDING_SUBSCRIPTION_UPGRADE ? 'text-warning-base' : 'text-md'">
      {{ statusText }}
    </span>
    <Tooltip
      v-if="row.status === UserStatus.PENDING_SUBSCRIPTION_UPGRADE"
      :text="upgradeTooltipText"
      :placement="Placement.RIGHT_TOP"
      info
      :skidding="-10">
      <InfoIcon class="cursor-help rounded-full outline-none text-vlt icon-xs" />
    </Tooltip>
  </div>
</template>
